import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import SupervisorFields from 'components/supervisor/SupervisorFields';
import FormSurface from 'components/shared/surfaces/FormSurface';
import UserEditHeader from 'components/shared/UserEditHeader';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import validateEditSupervisorForm from 'common/validations/form/editSupervisor';
import { updateSupervisorInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { generatePassword, getFieldsToUpdate } from 'common/form/utils';
import { supervisorFindFields } from 'common/constants/query-fields/supervisor';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import supervisorSlice from 'store/modules/supervisor';

const requiredFields = ['first_name', 'last_name', 'email', 'birthdate', 'ui_language'];

function SupervisorEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'common', 'routes']);
  const { id } = useParams();
  const pageTitle = t('supervisor.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const supervisor = useSelector((state) => state.supervisor.currentSupervisor);
  const status = useSelector((state) => state.supervisor.status.general);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten el mismo status; sin este guard el find inicial rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(supervisorSlice.actions.find({ fields: supervisorFindFields, id }));
  }, []);

  useEffect(() => {
    if (supervisor && Object.keys(supervisor).length > 0 && supervisor.id === id) {
      const userData = updateSupervisorInitialValues(supervisor);
      setInitialValues(userData);
    }
  }, [supervisor]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listSupervisor.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'user');
      hasSubmitted.current = true;
      dispatch(supervisorSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        mutators={{
          generatePassword
        }}
        onSubmit={onEdit}
        validate={validateEditSupervisorForm}
        render={({ form, handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <FormSurface>
              {/* Cabecera premium unificada: avatar (click amplia) + nombre + rol. */}
              <UserEditHeader
                photo={supervisor?.photo}
                firstName={supervisor?.first_name}
                lastName={supervisor?.last_name}
                email={supervisor?.email}
                roleLabel={t('roles.supervisor', { ns: 'common', defaultValue: 'Supervisor' })}
              />
              <SupervisorFields form={form} requiredFields={requiredFields} />
            </FormSurface>
            <Box sx={{ marginTop: '40px', textAlign: 'right' }}>
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listSupervisor.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('supervisor.edit', { ns: 'buttons' })}
                type="submit"
              />
            </Box>
          </form>
        )}
      />
    </>
  );
}

export default SupervisorEdit;
